import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { useContent, resolveUrl } from "@/lib/content";
import { SEO_PAGES, SITE_URL, SITE_NAME } from "@/lib/seo";
import { ImagePickerDialog } from "@/components/live/ImagePickerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Save, Search, ImagePlus, ExternalLink } from "lucide-react";

const Counter = ({ value, max }) => (
  <span className={`font-body text-xs ${value.length > max ? "text-lipstick-red" : "text-taupe"}`}>{value.length}/{max}</span>
);

const Preview = ({ title, description, path }) => (
  <div className="rounded-lg border border-sand bg-white p-4" data-testid="seo-preview">
    <p className="font-body text-xs text-jade-text truncate">{SITE_URL}{path === "/" ? "" : path}</p>
    <p className="font-body text-base text-[#1a0dab] leading-snug truncate">{title}</p>
    <p className="font-body text-sm text-stone-grey leading-snug line-clamp-2">{description}</p>
  </div>
);

const PageCard = ({ page, values, onChange }) => (
  <div className="glass rounded-xl p-6 space-y-4" data-testid={`seo-page-${page.key}`}>
    <div className="flex items-center justify-between gap-4">
      <h3 className="font-heading text-lg text-charcoal">{page.label} <span className="font-body text-xs text-taupe ml-2">{page.path}</span></h3>
      <label className="flex items-center gap-2 font-body text-xs text-stone-grey">
        <Switch checked={!values.noindex} onCheckedChange={(v) => onChange({ ...values, noindex: !v })} data-testid={`seo-index-switch-${page.key}`} />
        {values.noindex ? "Hidden from search" : "Indexed"}
      </label>
    </div>
    <div>
      <div className="flex justify-between mb-1"><Label className="font-body text-sm text-stone-grey">Page title</Label><Counter value={values.title} max={60} /></div>
      <Input data-testid={`seo-title-${page.key}`} value={values.title} onChange={(e) => onChange({ ...values, title: e.target.value })} className="form-input" />
    </div>
    <div>
      <div className="flex justify-between mb-1"><Label className="font-body text-sm text-stone-grey">Meta description</Label><Counter value={values.description} max={160} /></div>
      <Textarea data-testid={`seo-description-${page.key}`} rows={2} value={values.description} onChange={(e) => onChange({ ...values, description: e.target.value })} className="form-input" />
    </div>
    <Preview title={values.title} description={values.description} path={page.path} />
  </div>
);

export const SeoManager = () => {
  const { token } = useAuth();
  const { content, refresh } = useContent();
  const [site, setSite] = useState({ name: SITE_NAME, og_image: "", twitter: "", google_verification: "" });
  const [pages, setPages] = useState({});
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = content["seo.site"]?.metadata || {};
    setSite({ name: s.name || SITE_NAME, og_image: s.og_image || "", twitter: s.twitter || "", google_verification: s.google_verification || "" });
    setPages(Object.fromEntries(SEO_PAGES.map((p) => {
      const m = content[`seo.page.${p.key}`]?.metadata || {};
      return [p.key, { title: m.title || p.title, description: m.description || p.description, noindex: m.noindex ?? !!p.noindex }];
    })));
  }, [content]);

  const save = async () => {
    setSaving(true);
    try {
      const sections = { "seo.site": { content: "", metadata: site } };
      SEO_PAGES.forEach((p) => { sections[`seo.page.${p.key}`] = { content: "", metadata: pages[p.key] }; });
      await axios.put(`${API}/admin/content`, { sections }, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast.success("SEO settings saved");
    } catch (error) {
      toast.error("Failed to save SEO settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-seo">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal mb-2 flex items-center gap-3"><Search className="w-6 h-6 text-jade-text" /> SEO</h1>
          <p className="font-body text-stone-grey">Control how each page appears in Google and when shared on social media.</p>
        </div>
        <Button onClick={save} disabled={saving} className="btn-primary" data-testid="seo-save-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save all"}</Button>
      </div>

      <div className="glass rounded-xl p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="seo-site-settings">
        <div>
          <Label className="font-body text-sm text-stone-grey mb-1 block">Site name</Label>
          <Input data-testid="seo-site-name" value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} className="form-input" />
        </div>
        <div>
          <Label className="font-body text-sm text-stone-grey mb-1 block">X / Twitter handle</Label>
          <Input data-testid="seo-site-twitter" value={site.twitter} onChange={(e) => setSite({ ...site, twitter: e.target.value })} placeholder="@handle" className="form-input" />
        </div>
        <div>
          <Label className="font-body text-sm text-stone-grey mb-1 block">Social share image</Label>
          <div className="flex items-center gap-3">
            {site.og_image ? <img src={resolveUrl(site.og_image)} alt="" className="w-20 h-12 rounded object-cover bg-sand/30" data-testid="seo-og-preview" /> : <div className="w-20 h-12 rounded bg-sand/30" />}
            <Button type="button" variant="outline" size="sm" onClick={() => setPicker(true)} data-testid="seo-pick-og-image"><ImagePlus className="w-4 h-4 mr-1" /> Choose</Button>
            {site.og_image && <Button type="button" variant="ghost" size="sm" onClick={() => setSite({ ...site, og_image: "" })} data-testid="seo-clear-og-image">Remove</Button>}
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="font-body text-sm text-stone-grey mb-1 block">Google Search Console verification code</Label>
          <Input data-testid="seo-google-verification" value={site.google_verification} onChange={(e) => setSite({ ...site, google_verification: e.target.value })} placeholder="Paste the content=&quot;…&quot; value from the HTML tag method" className="form-input" />
          <p className="font-body text-xs text-taupe mt-2">
            Search Console → Add property → URL prefix <code>{SITE_URL}</code> → HTML tag. Then Sitemaps → submit <code>{SITE_URL}/api/seo/sitemap.xml</code>. Your current code is already built into the site; this field lets you change it without a developer.
          </p>
        </div>
        <p className="md:col-span-3 font-body text-xs text-taupe">
          Sitemap: <a className="underline inline-flex items-center gap-1" href={`${process.env.REACT_APP_BACKEND_URL}/api/seo/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="seo-sitemap-link">/api/seo/sitemap.xml <ExternalLink className="w-3 h-3" /></a>
          {" "}· robots.txt and the RTA adult-content label are already in place. Search crawlers can read your pages past the 18+ gate.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {SEO_PAGES.map((p) => pages[p.key] && (
          <PageCard key={p.key} page={p} values={pages[p.key]} onChange={(v) => setPages({ ...pages, [p.key]: v })} />
        ))}
      </div>

      <ImagePickerDialog open={picker} onClose={() => setPicker(false)} onSelect={({ url }) => setSite({ ...site, og_image: url })} title="Choose social share image" />
    </div>
  );
};
